import pluralize from "pluralize";
import PubSub from "pubsub-js";
import { allPass, any, clone, equals, filter, findIndex, map, prop, propEq, reject } from "ramda";
import { match, P } from "ts-pattern";
import { CalloutIntent } from "widgets";
import { filterIndexed } from "widgets/utils";
import { any as z_any } from "zod";
import type { StateCreator } from "zustand";

import { PubSubActions } from "@/global/pubsub-actions.ts";
import { type PermissionValidationResult, TrackerEnum } from "@/graphql.ts";
import { notify } from "@/modules/notifications/notify.tsx";
import { validatePublishingParameters } from "@/modules/workflow/graphql/queries.ts";
import type {
  WorkflowEditorStore,
  WorkflowErrorsSlice,
  WorkflowErrorsSliceState,
} from "@/modules/workflow/stores/workflow-editor-store-types";
import type {
  WorkflowErrorConnector,
  WorkflowErrorParameter,
  WorkflowParameter,
} from "@/modules/workflow/utils/types.ts";

export const INITIAL_PERMISSION_DATA: PermissionValidationResult = {
  inaccessibleSampleType: null,
  inaccessibleActivityResourcesData: [{ activityIdentifier: "", organisms: [], files: [] }],
};

export const initialErrorsSliceState: WorkflowErrorsSliceState = {
  errors: [],
  publishValidationErrors: INITIAL_PERMISSION_DATA,
  skipValidationForPlaybook: false, // Deactivated by default because we most likely use Processor to run workflows.
  statusBarMessage: null,
  sampleTypeMissing: false,
};

export const createErrorSlice: StateCreator<WorkflowEditorStore, [], [], WorkflowErrorsSlice> = (set, get) => {
  PubSub.subscribe(PubSubActions.LOGOUT, () => {
    set(initialErrorsSliceState);
  });

  return {
    ...initialErrorsSliceState,
    setSkipValidationForPlaybook(skip) {
      set({ skipValidationForPlaybook: skip });
      get().checkAllParameters();
    },
    setGlobalError(error) {
      match(error)
        .with({ type: "connection", message: null }, ({ nodeId, targetHandle }) => {
          const errors = reject(
            allPass([propEq(nodeId, "nodeId"), propEq(targetHandle, "targetHandle")]),
            get().errors,
          );
          set({ errors });
        })
        .with({ type: P.not("connection"), message: null }, ({ nodeId, parameterId }) => {
          const errors = reject(allPass([propEq(nodeId, "nodeId"), propEq(parameterId, "parameterId")]), get().errors);
          set({ errors });
        })
        .otherwise((error) => {
          if (error.nodeId && error.message) {
            const errors = clone(get().errors);
            let idx = -1;

            if (error.type !== "connection" && error.parameterId) {
              idx = findIndex(
                allPass([propEq(error.nodeId, "nodeId"), propEq(error.parameterId, "parameterId")]),
                errors,
              );
            }

            if (error.type === "connection" && error.targetHandle) {
              idx = findIndex(
                allPass([propEq(error.nodeId, "nodeId"), propEq(error.targetHandle, "targetHandle")]),
                errors,
              );
            }

            if (idx >= 0) {
              errors[idx].message = error.message;
            } else {
              errors.push(error as WorkflowErrorParameter | WorkflowErrorConnector);
            }

            set({ errors });
          }
        });
    },
    checkAllParameters(nodeId, updateSkipPlaybookTo) {
      // Sync stored parameter list with params used in all nodes
      get().cleanAllParameters();

      const setGlobalError = get().setGlobalError;
      let hasErrors = false;

      // Remove all errors first
      get().resetActivityNodeErrors(nodeId);

      // Check Parameters
      const parameters = get().getParameters(nodeId);

      if (updateSkipPlaybookTo != undefined) {
        set({ skipValidationForPlaybook: updateSkipPlaybookTo });
      }
      const skipValidationForPlaybook = get().skipValidationForPlaybook;

      for (const parameter of parameters) {
        let validationSchema = parameter.schema;

        if (skipValidationForPlaybook && parameter.staged) {
          // The sample parameter is complex, we just let it pass.
          // Some more validation will be done before publishing anyways.
          // So we're not at risk here.
          if (parameter.typeName === "SampleParam") {
            if (validationSchema != undefined) {
              validationSchema = z_any();
            }
          } else {
            validationSchema = validationSchema?.nullish();
          }
        }

        const result = validationSchema?.safeParse(parameter.values.extractNullable());
        match(result).with({ success: false, error: P.select() }, (error) => {
          hasErrors = true;
          setGlobalError({
            type:
              parameter.typeName === "TrackerParam" && parameter.tracker === TrackerEnum.Output
                ? "tracker"
                : parameter.typeName === "SampleParam"
                  ? "sample"
                  : "parameter",
            nodeId: parameter.nodeId,
            parameterId: parameter.parameterId,
            message: error.issues[0].message,
            code: error.issues[0].params?.code ?? error.issues[0].code,
          });
        });
      }

      // Check Tracker (all parameters)
      hasErrors = get().checkTrackerParameters();
      hasErrors = get().checkConnections(nodeId);

      return hasErrors;
    },
    checkSingleParameter(nodeId, parameterId) {
      const parameter = get()
        .getParameters(nodeId)
        .find((param) => param.parameterId === parameterId);
      const skipValidationForPlaybook = get().skipValidationForPlaybook;
      let hasErrors = false;

      if (parameter != undefined) {
        let validationSchema = parameter.schema;

        if (skipValidationForPlaybook && parameter.staged) {
          validationSchema = validationSchema?.nullish();
        }

        const result = validationSchema?.safeParse(parameter.values.extractNullable());

        match(result)
          .with({ success: false, error: P.select() }, (error) => {
            hasErrors = true;
            get().setGlobalError({
              type:
                parameter.typeName === "TrackerParam" && parameter.tracker === TrackerEnum.Output
                  ? "tracker"
                  : parameter.typeName === "SampleParam"
                    ? "sample"
                    : "parameter",
              nodeId: parameter.nodeId,
              parameterId: parameter.parameterId,
              message: error.issues[0].message,
              code: error.issues[0].params?.code ?? error.issues[0].code,
            });
          })
          .otherwise(() => {
            // Clean error if any
            const errors = reject(
              (error) => equals(error.nodeId, nodeId) && equals(error.parameterId, parameterId),
              get().errors,
            );
            set({ errors });
          });
      }

      return hasErrors;
    },
    checkTrackerParameters() {
      const setGlobalError = get().setGlobalError;
      let hasErrors = false;

      const trackerParameters = get()
        .getParameters()
        .filter((parameter) => parameter.typeName === "TrackerParam" && parameter.tracker === TrackerEnum.Output);
      const attributeIds = filter(
        (obj): obj is number => obj != undefined,
        map((obj) => obj.values.extractNullable(), trackerParameters),
      );
      const duplicateAttributeIds = filterIndexed((id, index) => attributeIds.indexOf(id) !== index, attributeIds);
      const duplicateParameters = filter(
        (obj) => duplicateAttributeIds.includes(obj.values.extractNullable() as number),
        trackerParameters,
      );

      for (const { nodeId, parameterId } of duplicateParameters) {
        hasErrors = true;
        setGlobalError({
          type: "tracker",
          message: "Warning: This attribute was selected in a different activity, so the data may be overwritten.",
          nodeId,
          parameterId,
        });
      }

      return hasErrors;
    },
    checkConnections(nodeId) {
      const setGlobalError = get().setGlobalError;
      const nodes = nodeId == null ? get().nodes : filter((node) => node.id === nodeId, get().nodes);
      let hasErrors = false;

      // Remove all "connection" issues
      if (nodeId == null) {
        const errors = reject((error) => error.type === "connection", get().errors);
        set({ errors });
      } else {
        const errors = reject((error) => error.type === "connection" && error.nodeId === nodeId, get().errors);
        set({ errors });
      }

      for (const node of nodes) {
        if (!node.data.disabled) {
          const activity = get().getActivity(node.data.activityName, node.data.version);

          if (activity?.inputs) {
            for (const input of activity.inputs) {
              const connections = filter(
                (edge) => edge.target === node.id && edge.targetHandle === input.id,
                get().edges,
              );

              if (input.optional === true && connections.length === 0) {
                // If a connection is min == 2 and optional, it means that it either has no connections or at least 2
                continue;
              }

              // Too few connections. minConnections > 0 makes it mandatory-ish.
              if (connections.length > 0 && connections.length < input.minConnections) {
                const missingQtty = input.minConnections - connections.length;
                setGlobalError({
                  type: "connection",
                  nodeId: node.id,
                  targetHandle: input.id,
                  message:
                    input.minConnections > 1 && missingQtty > 0
                      ? `Input '${input.label}' is missing ${missingQtty} ${pluralize("connection", missingQtty)}.`
                      : `Input '${input.label}' must be connected.`,
                });
                hasErrors = true;
                continue;
              }

              // Too many connections
              if (input.maxConnections != undefined && connections.length > input.maxConnections) {
                setGlobalError({
                  type: "connection",
                  nodeId: node.id,
                  targetHandle: input.id,
                  message: `Only ${input.maxConnections} ${pluralize(
                    "connection",
                    input.maxConnections,
                  )} allowed at a time.`,
                });
                hasErrors = true;
                continue;
              }

              // Mandatory, just needs connection
              if (input.optional === false && connections.length === 0) {
                setGlobalError({
                  type: "connection",
                  nodeId: node.id,
                  targetHandle: input.id,
                  message: `Input '${input.label}' must be connected.`,
                });
                hasErrors = true;
              }
            }
          }
        }
      }

      return hasErrors;
    },
    resetActivityNodeErrors(nodeId) {
      let __keys = map(
        prop("id"),
        filter((node) => node.type === "activity", get().nodes),
      );

      if (nodeId) {
        __keys = filter((key) => key === nodeId, __keys);
      }

      const errors = reject((error) => any(equals(error.nodeId), __keys), get().errors);

      set({ errors });
    },
    cleanAllParameters() {
      const nodeIds = get().nodes.map((node) => node.id);
      const currentRecord = get().parametersRecord;
      const cleanedParametersRecord: Record<string, WorkflowParameter[]> = {};

      // Only keep parameters for nodes that still exist
      nodeIds.forEach((nodeId) => {
        // iterate through current nodes instead of all parameters for performance
        if (currentRecord[nodeId]) {
          cleanedParametersRecord[nodeId] = currentRecord[nodeId];
        }
      });

      set({ parametersRecord: cleanedParametersRecord });
    },
    setStatusBarMessage(message, intent, timeout) {
      const { statusBarMessage } = get();
      const prevTimeoutId = statusBarMessage?.timeoutId;

      if (prevTimeoutId) {
        window.clearTimeout(prevTimeoutId);
      }

      let newStatusBarMessage: WorkflowEditorStore["statusBarMessage"] =
        message == null ? null : { message, intent: intent ?? CalloutIntent.None, timeoutId: undefined };

      if (timeout) {
        const timeoutId = window.setTimeout(() => set({ statusBarMessage: null }), timeout);
        newStatusBarMessage = { ...newStatusBarMessage!, timeoutId: timeoutId };
      }

      set({ statusBarMessage: newStatusBarMessage });
    },
    async validateParametersForPublishing() {
      const workflowEventId = get().workflowEvent.id;
      if (workflowEventId) {
        const response = await validatePublishingParameters({ workflowEventId }).catch(() => {
          notify.error({ title: "Validation Query Failed", children: "Failed to validate parameter settings" });
          set({ publishValidationErrors: INITIAL_PERMISSION_DATA });
        });
        const errors = response?.data?.workflow?.publishPermissionCheck as unknown as PermissionValidationResult;
        set({ publishValidationErrors: errors });
      }
    },
  };
};
